﻿﻿<script lang="ts" setup>
import { inject, reactive, watch } from 'vue'
import ErrorHelper from '../../exports/error'
import { storeToRefs } from 'pinia'
import { useCommonStore } from '@/stores/CommonStore'
import { useLoginStore } from '@/stores/LoginStore'
import type {
  ILoginRequest,
  ITrackingIdentifyWithIdFunction
} from '@/models/interfaces'
import MFA from './components/MfaModalComponent.vue'
import { type RouteLocationRaw, useRoute, useRouter } from 'vue-router'
import { useForm } from 'vee-validate'
import TextInput from '@/components/library/TextInput.vue'
import { useMessagesStore } from '@/stores/MessagesStore'
import { getDefaultRouteForUser } from '@/router/accessPolicy'
import { SIGNUP_ROUTE_NAME } from '@/pages/signup/config/signup.constants'
import { I18nT, useI18n } from 'vue-i18n'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { useZendeskWidget } from '@/composables/useZendeskWidget'
import { useFirebaseSession } from '@/composables/useFirebaseSession'
import { useError } from '@/composables/useError'
import { isDifferentDraftOwner } from '@/pages/messages/services/messagesDrafts.repository'

const { t } = useI18n({ useScope: 'global' })

const trackingIdentify = inject<ITrackingIdentifyWithIdFunction>(
  '$trackingIdentifyWithId'
) as ITrackingIdentifyWithIdFunction

const route = useRoute()
const router = useRouter()
const state = reactive({
  localEmail: '',
  localPassword: '',
  email: '',
  password: '',
  submitError: false,
  submitErrorMessage: '',
  signInBtnText: t('login.signInButton'),
  submitting: false,
  zendeskScript: null as HTMLScriptElement | null
})

const loginStore = useLoginStore()
const { showMfaModal, showLoginErrorMsg, loginError, loggingOut } =
  storeToRefs(loginStore)
const { login, setShowMfaModal } = loginStore

const commonStore = useCommonStore()
const {
  isOnline,
  isDesktopWidth,
  isUserMatched,
  fullUserInfo,
  isRestricted
} = storeToRefs(commonStore)
const { fetchFullUserInfo } = commonStore

const messagesStore = useMessagesStore()
const { setSelectedMessageThread, clearReplyDrafts } = messagesStore

useZendeskWidget()

const { initializeFirebaseAuth } = useFirebaseSession()
const { handleError } = useError()

const formValidationSchema = { email: 'required|email', password: 'required' }

const { meta, validate, setErrors } = useForm({
  validationSchema: formValidationSchema
})

async function resetLoginErrorMsg(): Promise<void> {
  loginError.value = ''
}

async function submitLogin(): Promise<void> {
  const { valid } = await validate()
  if (valid) {
    state.submitting = true
    state.email = state.localEmail
    state.password = state.localPassword
    state.submitError = false
    state.signInBtnText = t('login.loginStatus')
    try {
      const payload: ILoginRequest = {
        username: state.localEmail,
        password: state.localPassword,
        returnUrl: route.query?.returnTo?.toString()
      }

      tryLogin(payload)
    } catch (e) {
      ErrorHelper.handleError(e, 'submitLogin')

      state.signInBtnText = t('login.signInButton')
      return
    }
  }
}

function tryLogin(payload: ILoginRequest): void {
  login(payload).then((response: string) => {
    if (response == 'success') {
      fetchFullUserInfo().then(async (success) => {
        if (!success) {
          // there was an error
          router.push({ name: 'logout' })
          return
        } else {
          //SHOULD have fullUserInfo now to check for case id
          //case id will be 0 if unmatched
          if (!isUserMatched.value && !loggingOut.value) {
            trackingIdentify({ email: payload.username || '' })
            // Establish Firebase session before routing into signup match
            // so prematch listener can attach immediately
            const fbAuthSuccess = await initializeFirebaseAuth()
            if (!fbAuthSuccess) {
              handleError({
                error: new Error(
                  'Firebase auth failed during post-login signup routing'
                ),
                action: 'initializeFirebaseAuth',
                showModal: false
              })
            }
            router.push(getPostLoginRoute())
            return
          }

          if (isRestricted.value) {
            router.push(getPostLoginRoute())
            return
          }

          if (isUserMatched.value) {
            cacheSecurityCheck()
            handleDraftSecurityCheck()
          }
        }
      })
    } else {
      if (showLoginErrorMsg.value) {
        setErrors({ password: loginError.value })
      }
      state.signInBtnText = t('login.signInButton')
      state.submitting = false
    }
  })
}

function close(): void {
  state.signInBtnText = t('login.signInButton')
  setShowMfaModal(false)
}

function getPostLoginRoute(): RouteLocationRaw {
  const returnTo = route.query?.returnTo?.toString() || null

  return getDefaultRouteForUser(
    {
      hasMatch: Boolean(isUserMatched.value),
      isRestricted: Boolean(isRestricted.value)
    },
    returnTo
  )
}

function cacheSecurityCheck(): void {
  // always clear selected thread and in-memory drafts on login
  setSelectedMessageThread({})
  clearReplyDrafts()
}

function handleDraftSecurityCheck(): void {
  const currentUserId = fullUserInfo.value.userId
  if (currentUserId && isDifferentDraftOwner(currentUserId)) {
    // IndexedDB cleanup deferred to MessagesPage init where indexedDbService is initialized.
    // In-memory drafts are already cleared by cacheSecurityCheck above.
    clearReplyDrafts()
  }
  navigate()
}

function navigate(): void {
  router.push(getPostLoginRoute())
}
</script>

<template>
  <div>
    <header v-if="isDesktopWidth" class="border-bottom pb-1">
      <h1 class="title text-center">
        <I18nT keypath="login.title">
          <template #sr>
            <span class="sr-only">{{ t('login.titleSr') }}</span>
          </template>
        </I18nT>
      </h1>
    </header>
    <form
      class="flex-column"
      :class="isDesktopWidth ? 'mt-3 gap-4' : 'gap-5'"
      @submit.prevent="submitLogin"
    >
      <div v-if="!isOnline" class="tp-offline-container">
        {{ t('login.offlineError') }}
      </div>
      <div class="stack" :class="isDesktopWidth ? 'size-1' : 'size-2'">
        <TextInput
          v-model="state.localEmail"
          name="email"
          type="text"
          :label="t('login.emailLabel')"
          placeholder=""
          success-message=""
          aria-describedby="NameTip"
          :focus="true"
          :required="true"
          :disabled="showMfaModal"
          @input="resetLoginErrorMsg"
        />
        <TextInput
          v-model="state.localPassword"
          name="password"
          type="password"
          :label="t('login.passwordLabel')"
          placeholder=""
          success-message=""
          aria-describedby="NameTip"
          :required="true"
          :disabled="showMfaModal"
          :password-visibility-option="true"
          autocomplete="off"
          @input="resetLoginErrorMsg"
        />
      </div>
      <div class="stack" :class="isDesktopWidth ? 'size-4' : 'login-ctas'">
        <div class="stack size-1">
          <RouterLink
            to="/forgotEmailOrPassword"
            class="fit-content mx-auto brand underline"
            :class="isDesktopWidth ? 'font-size-1' : 'font-size-0'"
            >{{ t('login.forgotPasswordCta') }}
          </RouterLink>
          <a
            v-if="!isDesktopWidth"
            :class="{
              'fit-content mx-auto pb-1 brand font-size-0': !isDesktopWidth
            }"
            target="_blank"
            href="https://support.talkingparents.com/hc/en-us/requests/new"
          >
            <FontAwesomeIcon class="icon-size-1" icon="fa-light fa-envelope" />
            {{ t('login.contactUsCta') }}
          </a>
        </div>
        <div class="stack size-1" :class="{ 'mx-1': isDesktopWidth }">
          <button
            id="sign_in"
            type="submit"
            class="btn primary w-100"
            :class="{
              disabled: !meta.dirty || !meta.valid || state.submitting
            }"
            :disabled="!meta.dirty || !meta.valid || state.submitting"
          >
            {{ state.signInBtnText }}
          </button>
          <button
            type="button"
            class="btn secondary w-100"
            :class="{ disabled: state.submitting }"
            :disabled="state.submitting"
            @click="router.push({ name: SIGNUP_ROUTE_NAME })"
          >
            {{ t('login.signupCta') }}
          </button>
        </div>
      </div>
    </form>
    <MFA
      :modal-active="showMfaModal"
      :email="state.email"
      :password="state.password"
      @close="close"
      @submit-login="tryLogin"
    ></MFA>
  </div>
</template>

<style scoped>
.title {
  color: light-dark(var(--brand-10), white);
}

.tp-offline-container {
  border: 3px solid red;
  padding: 0.5rem;
  margin-bottom: 1rem;
  border-radius: var(--radius-4);
  text-align: center;
}

:deep(label) {
  color: light-dark(var(--gray-10), white);
}
:deep(input) {
  color: var(--text-0);
}
.login-ctas {
  --space: 6rem;
}
</style>
